import React, { useState } from "react";
import { Box, FormControl, InputAdornment } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import {
  BaseStyledOutlinedInput,
  BaseStyledInputLabel,
  LABEL_TRANSFORM,
} from "../common/BaseInputStyles";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import styled from "styled-components";
import { inToFtAndIn, ftAndInToIn } from "../../../questionnaire/utils";

interface NumericFormatCustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const StyledOutlinedInput = styled(BaseStyledOutlinedInput)({
  "& .MuiOutlinedInput-input": {
    width: "30%", // Leave space for unit
  },
});

const NumericFormatAdapter = React.forwardRef<
  HTMLInputElement,
  NumericFormatCustomProps & Partial<NumericFormatProps>
>(function NumericFormatAdapter(props, ref) {
  const { onChange, name, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: name,
            value: values.value,
          },
        });
      }}
      thousandSeparator={false}
      decimalSeparator="."
      allowNegative={false}
      valueIsNumericString={true}
      isAllowed={(values) => {
        const { floatValue } = values;
        // Allow empty or valid positive numbers
        return floatValue === undefined || floatValue >= 0;
      }}
    />
  );
});

interface HeightInputImperialProps {
  value: number | null; // Value in total inches
  onChange: (totalInches: number | null) => void;
  name?: string;
}

const HeightInputImperial = ({
  value,
  onChange,
  name = "height-imperial",
}: HeightInputImperialProps) => {
  const theme: Theme = useTheme();
  const [focusedField, setFocusedField] = useState<"ft" | "in" | null>(null);

  // Convert total inches to ft and in for display
  const displayValues = value !== null ? inToFtAndIn(value) : { value1: 0, value2: 0 };
  const ftValue = displayValues.value1;
  const inValue = displayValues.value2;

  const handleFtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFt = e.target.value ? Number(e.target.value) : 0;
    const totalInches = ftAndInToIn(newFt, inValue);
    onChange(totalInches);
  };

  const handleInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newIn = e.target.value ? Number(e.target.value) : 0;
    // Limit inches to 0-11
    if (newIn > 11) return;
    const totalInches = ftAndInToIn(ftValue, newIn);
    onChange(totalInches);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "8px",
        width: "100%",
      }}
    >
      {/* Feet Input */}
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(e) => e.preventDefault()}
        sx={{ flex: 1 }}
      >
        <FormControl
          variant="outlined"
          fullWidth
          sx={{
            position: "relative",
            height: "56px",
            minHeight: "56px",
          }}
        >
          <BaseStyledInputLabel
            htmlFor={`${name}-ft`}
            theme={theme}
            style={{
              transform:
                ftValue > 0 || focusedField === "ft"
                  ? LABEL_TRANSFORM.shrunk
                  : LABEL_TRANSFORM.normal,
            }}
          >
            Height (ft)
          </BaseStyledInputLabel>

          <StyledOutlinedInput
            id={`${name}-ft`}
            value={ftValue > 0 ? ftValue.toString() : ""}
            inputComponent={NumericFormatAdapter as any}
            inputProps={{
              name: `${name}-ft`,
            }}
            onFocus={() => setFocusedField("ft")}
            onBlur={() => setFocusedField(null)}
            onChange={handleFtChange as any}
            theme={theme}
            endAdornment={
              (ftValue > 0 || focusedField === "ft") ? (
                <InputAdornment
                  position="end"
                  sx={{ marginTop: "16px", marginLeft: "8px" }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-Medium)",
                      fontSize: "14px",
                      color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                    }}
                  >
                    ft
                  </span>
                </InputAdornment>
              ) : null
            }
          />
        </FormControl>
      </Box>

      {/* Inches Input */}
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(e) => e.preventDefault()}
        sx={{ flex: 1 }}
      >
        <FormControl
          variant="outlined"
          fullWidth
          sx={{
            position: "relative",
            height: "56px",
            minHeight: "56px",
          }}
        >
          <BaseStyledInputLabel
            htmlFor={`${name}-in`}
            theme={theme}
            style={{
              transform:
                inValue > 0 || focusedField === "in"
                  ? LABEL_TRANSFORM.shrunk
                  : LABEL_TRANSFORM.normal,
            }}
          >
            Height (in)
          </BaseStyledInputLabel>

          <StyledOutlinedInput
            id={`${name}-in`}
            value={inValue > 0 ? inValue.toString() : ""}
            inputComponent={NumericFormatAdapter as any}
            inputProps={{
              name: `${name}-in`,
            }}
            onFocus={() => setFocusedField("in")}
            onBlur={() => setFocusedField(null)}
            onChange={handleInChange as any}
            theme={theme}
            endAdornment={
              (inValue > 0 || focusedField === "in") ? (
                <InputAdornment
                  position="end"
                  sx={{ marginTop: "16px", marginLeft: "8px" }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-Medium)",
                      fontSize: "14px",
                      color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
                    }}
                  >
                    in
                  </span>
                </InputAdornment>
              ) : null
            }
          />
        </FormControl>
      </Box>
    </Box>
  );
};

export default HeightInputImperial;
